<template>
  <div class="viewer-wrapper">
    <div ref="canvasContainer" class="canvas-container" @contextmenu.prevent />

    <!-- 操作提示 -->
    <div class="controls-hint">
      <span>滚轮：缩放</span>
      <span>右键拖拽：旋转</span>
      <span>左键拖拽：平移</span>
      <span>点击标注：聚焦局部</span>
    </div>

    <!-- 剖面控制 -->
    <div v-if="clipEnabled" class="clip-slider">
      <label>剖面位置</label>
      <input
        type="range"
        min="-1.5"
        max="1.5"
        step="0.01"
        :value="clipOffset"
        @input="onClipChange($event.target.value)"
      />
    </div>

    <!-- 病变对比面板 -->
    <transition name="slide">
      <div v-if="selectedLesion" class="lesion-panel">
        <div class="lesion-header">
          <h3>{{ selectedLesion.name }}</h3>
          <button class="close-btn" @click="closeLesionPanel">×</button>
        </div>
        <p class="lesion-desc">{{ selectedLesion.description }}</p>
        <div class="compare-toggle">
          <button
            :class="{ active: compareMode === 'lesion' }"
            @click="setCompareMode('lesion')"
          >
            病变状态
          </button>
          <button
            :class="{ active: compareMode === 'normal' }"
            @click="setCompareMode('normal')"
          >
            正常对照
          </button>
          <button
            :class="{ active: compareMode === 'split' }"
            @click="setCompareMode('split')"
          >
            对比视图
          </button>
        </div>
        <div class="compare-content">
          <div v-if="compareMode === 'lesion' || compareMode === 'split'" class="compare-block lesion">
            <h4>病变</h4>
            <p>{{ selectedLesion.lesionDescription }}</p>
          </div>
          <div v-if="compareMode === 'normal' || compareMode === 'split'" class="compare-block normal">
            <h4>正常</h4>
            <p>{{ selectedLesion.normalDescription }}</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- 选中组件信息 -->
    <div v-if="hoveredComponent" class="component-tooltip">
      {{ hoveredComponent }}
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import {
  createScene,
  applyClippingToModel,
  animateCameraTo,
  fitCameraToBounds,
  pickObject,
  pulseHotspots,
} from '@/utils/threeHelpers'

export default {
  name: 'AnatomyViewer',
  props: {
    modelData: { type: Object, default: null },
    clipEnabled: { type: Boolean, default: false },
    clipOffset: { type: Number, default: 0 },
    clipAxis: { type: String, default: 'z' },
    visibleComponents: { type: Object, default: () => ({}) },
    compareMode: { type: String, default: 'lesion' },
    selectedLesionId: { type: String, default: null },
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      clipPlane: null,
      animationId: null,
      currentRoot: null,
      hoveredComponent: null,
      selectedLesion: null,
      raycastTargets: [],
      defaultCamera: null,
      _labelProjectVec: null,
    }
  },
  watch: {
    modelData: {
      handler(val) {
        if (val && this.scene) this.loadModel(val)
      },
    },
    clipEnabled(val) {
      if (this.currentRoot && this.clipPlane) {
        applyClippingToModel(this.currentRoot, this.clipPlane, val)
        if (this.scene) {
          const helper = this.scene.children.find((c) => c.isPlaneHelper)
          if (helper) helper.visible = val
        }
      }
    },
    clipOffset(val) {
      if (this.clipPlane) {
        this.clipPlane.constant = parseFloat(val)
      }
    },
    clipAxis(axis) {
      if (!this.clipPlane) return
      const normals = {
        x: new THREE.Vector3(1, 0, 0),
        y: new THREE.Vector3(0, 1, 0),
        z: new THREE.Vector3(0, 0, 1),
      }
      this.clipPlane.normal.copy(normals[axis] || normals.z)
      if (this.currentRoot) {
        applyClippingToModel(this.currentRoot, this.clipPlane, this.clipEnabled)
      }
    },
    visibleComponents: {
      deep: true,
      handler(map) {
        if (!this.modelData) return
        this.modelData.components.forEach((comp) => {
          if (map[comp.id] === undefined) return
          comp.visible = map[comp.id]
          const targets = comp.objects && comp.objects.length ? comp.objects : [comp.mesh]
          targets.filter(Boolean).forEach((obj) => {
            obj.visible = map[comp.id]
          })
        })
      },
    },
    compareMode(mode) {
      this.updateLesionVisibility(mode)
    },
    selectedLesionId(id) {
      if (!id || !this.modelData) {
        this.selectedLesion = null
        return
      }
      const lesion = this.modelData.lesions.find((l) => l.id === id)
      if (lesion) {
        this.selectedLesion = lesion
        this.focusLesion(lesion)
        this.updateLesionVisibility(this.compareMode || 'split')
      }
    },
  },
  mounted() {
    this.initThree()
    if (this.modelData) this.loadModel(this.modelData)
    window.addEventListener('resize', this.onResize)
    this.$refs.canvasContainer.addEventListener('click', this.onCanvasClick)
    this.$refs.canvasContainer.addEventListener('mousemove', this.onCanvasMove)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.onResize())
      this.resizeObserver.observe(this.$refs.canvasContainer)
    }
    this.$nextTick(() => {
      this.onResize()
      this.ensureCanvasSize()
    })
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    window.removeEventListener('resize', this.onResize)
    if (this.$refs.canvasContainer) {
      this.$refs.canvasContainer.removeEventListener('click', this.onCanvasClick)
      this.$refs.canvasContainer.removeEventListener('mousemove', this.onCanvasMove)
    }
    if (this.animationId) cancelAnimationFrame(this.animationId)
    this.clearHotspotLabels()
    if (this.renderer) {
      this.renderer.dispose()
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
      }
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer
      const ctx = createScene(container)
      this.scene = ctx.scene
      this.camera = ctx.camera
      this.renderer = ctx.renderer
      this.controls = ctx.controls
      this.clipPlane = ctx.clipPlane
      this._labelProjectVec = new THREE.Vector3()
      this.animate()
    },

    loadModel(modelData) {
      if (!this.scene) return
      this.clearHotspotLabels()
      if (this.currentRoot) {
        this.scene.remove(this.currentRoot)
        this.disposeObject(this.currentRoot)
      }
      this.currentRoot = modelData.root
      this.scene.add(this.currentRoot)
      applyClippingToModel(this.currentRoot, this.clipPlane, this.clipEnabled)
      this.buildRaycastTargets(modelData)
      const fitBox = this.getModelFitBox(modelData)
      this.defaultCamera = fitCameraToBounds(this.camera, this.controls, fitBox)
      this.selectedLesion = null
      this.$emit('model-loaded', modelData)
      this.setupHotspotLabels(modelData)
      this.$nextTick(() => this.onResize())
    },

    buildRaycastTargets(modelData) {
      this.raycastTargets = []
      modelData.hotspots.forEach((hs) => this.raycastTargets.push(hs.group))
      modelData.lesions.forEach((l) => {
        if (l.lesionMesh) this.raycastTargets.push(l.lesionMesh)
        if (l.normalMesh) this.raycastTargets.push(l.normalMesh)
      })
      modelData.root.traverse((obj) => {
        if (obj.userData.isHotspot || obj.userData.isLesionMarker) {
          this.raycastTargets.push(obj)
        }
      })
    },

    getModelFitBox(modelData) {
      if (modelData.bounds && modelData.bounds.box) {
        return modelData.bounds.box.clone()
      }
      const box = new THREE.Box3()
      const body = modelData.root.getObjectByName('body')
      if (body) {
        box.setFromObject(body)
      } else {
        box.setFromObject(modelData.root)
      }
      return box
    },

    disposeObject(obj) {
      obj.traverse((child) => {
        if (child.geometry) child.geometry.dispose()
        if (child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material]
          mats.forEach((m) => m.dispose())
        }
      })
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate)
      const time = performance.now()
      if (this.modelData) pulseHotspots(this.modelData.hotspots, time)
      if (this.controls) this.controls.update()
      this.updateHotspotLabelPositions()
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera)
      }
    },

    setupHotspotLabels(modelData) {
      const container = this.$refs.canvasContainer
      if (!container || !modelData?.hotspots) return
      modelData.hotspots.forEach((hs) => {
        const el = document.createElement('div')
        el.className = 'anatomy-hotspot-label'
        el.textContent = hs.label
        if (hs.lesionId) {
          el.classList.add('anatomy-hotspot-label--clickable')
          el.addEventListener('click', (event) => {
            event.stopPropagation()
            this.openLesionPanel(hs.lesionId)
          })
        }
        container.appendChild(el)
        hs.labelEl = el
      })
    },

    updateHotspotLabelPositions() {
      const container = this.$refs.canvasContainer
      if (!container || !this.camera || !this.modelData?.hotspots) return
      const w = container.clientWidth
      const h = container.clientHeight
      if (!w || !h) return
      const v = this._labelProjectVec
      this.modelData.hotspots.forEach((hs) => {
        if (!hs.labelEl || !hs.group) return
        hs.group.getWorldPosition(v)
        v.project(this.camera)
        if (v.z > 1) {
          hs.labelEl.style.visibility = 'hidden'
          return
        }
        hs.labelEl.style.visibility = 'visible'
        hs.labelEl.style.left = `${(v.x * 0.5 + 0.5) * w + 7}px`
        hs.labelEl.style.top = `${(-v.y * 0.5 + 0.5) * h}px`
      })
    },

    clearHotspotLabels() {
      const container = this.$refs.canvasContainer
      if (container) {
        container.querySelectorAll('.anatomy-hotspot-label').forEach((el) => el.remove())
      }
      if (this.modelData?.hotspots) {
        this.modelData.hotspots.forEach((hs) => {
          hs.labelEl = null
        })
      }
    },

    onResize() {
      const container = this.$refs.canvasContainer
      if (!container || !this.camera || !this.renderer) return
      const w = container.clientWidth
      const h = container.clientHeight
      if (w === 0 || h === 0) return
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(w, h)
    },

    fitDefaultCamera() {
      if (!this.camera || !this.controls || !this.modelData) return
      const fitBox = this.getModelFitBox(this.modelData)
      this.defaultCamera = fitCameraToBounds(this.camera, this.controls, fitBox)
    },

    ensureCanvasSize(attempt = 0) {
      const container = this.$refs.canvasContainer
      if (!container || !this.camera || !this.renderer) return
      if (container.clientWidth > 0 && container.clientHeight > 0) {
        this.onResize()
        return
      }
      if (attempt < 30) {
        requestAnimationFrame(() => this.ensureCanvasSize(attempt + 1))
      }
    },

    onCanvasClick(event) {
      const hits = pickObject(event, this.$refs.canvasContainer, this.camera, this.raycastTargets)
      if (!hits.length) return
      let obj = hits[0].object
      while (obj && !obj.userData.isHotspot && !obj.userData.isHotspotDot && !obj.userData.isLesionMarker) {
        obj = obj.parent
      }
      if (!obj) return

      if (obj.userData.isHotspot || obj.userData.isHotspotDot) {
        this.onHotspotClick(obj.userData)
      } else if (obj.userData.isLesionMarker) {
        this.openLesionPanel(obj.userData.lesionId)
      }
    },

    openLesionPanel(lesionId) {
      if (!lesionId || !this.modelData) return
      const lesion = this.modelData.lesions.find((l) => l.id === lesionId)
      if (!lesion) return
      this.selectedLesion = lesion
      this.$emit('lesion-select', lesionId)
      this.$emit('update:compareMode', 'split')
      this.focusLesion(lesion)
      this.updateLesionVisibility('split')
    },

    onHotspotClick(data) {
      animateCameraTo(this.camera, this.controls, data.cameraTarget, data.cameraDistance)
      if (data.lesionId) {
        this.openLesionPanel(data.lesionId)
      }
      this.$emit('hotspot-click', data)
    },

    onCanvasMove(event) {
      if (!this.modelData) return
      const anatomyMeshes = []
      this.modelData.components.forEach((comp) => {
        if (comp.objects && comp.objects.length) {
          anatomyMeshes.push(...comp.objects)
        } else if (comp.mesh) {
          anatomyMeshes.push(comp.mesh)
        }
      })
      const hits = pickObject(event, this.$refs.canvasContainer, this.camera, anatomyMeshes)
      if (hits.length) {
        const comp = hits[0].object.userData.componentName
        this.hoveredComponent = comp || null
      } else {
        this.hoveredComponent = null
      }
    },

    focusLesion(lesion) {
      animateCameraTo(this.camera, this.controls, lesion.cameraTarget, lesion.cameraDistance)
    },

    updateLesionVisibility(mode) {
      if (!this.modelData) return
      this.modelData.lesions.forEach((l) => {
        const isSelected = this.selectedLesion && l.id === this.selectedLesion.id
        if (!isSelected) {
          if (l.normalMesh) l.normalMesh.visible = false
          if (l.lesionMesh) l.lesionMesh.visible = false
          if (l.ringMesh) l.ringMesh.visible = false
          return
        }
        if (l.normalMesh) l.normalMesh.visible = mode === 'normal' || mode === 'split'
        if (l.lesionMesh) l.lesionMesh.visible = mode === 'lesion' || mode === 'split'
        if (l.ringMesh) l.ringMesh.visible = mode === 'lesion' || mode === 'split'
      })
    },

    setCompareMode(mode) {
      this.$emit('update:compareMode', mode)
    },

    closeLesionPanel() {
      this.selectedLesion = null
      this.$emit('lesion-select', null)
      this.updateLesionVisibility(this.compareMode)
    },

    onClipChange(val) {
      this.$emit('update:clipOffset', parseFloat(val))
    },

    resetCamera() {
      if (!this.controls || !this.camera) return
      if (this.defaultCamera) {
        animateCameraTo(
          this.camera,
          this.controls,
          this.defaultCamera.center,
          this.defaultCamera.distance
        )
        return
      }
      this.fitDefaultCamera()
    },
  },
}
</script>

<style scoped>
.viewer-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: grab;
}

.canvas-container:active {
  cursor: grabbing;
}

.controls-hint {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  font-size: 12px;
  color: #94a3b8;
  pointer-events: none;
  backdrop-filter: blur(8px);
}

.clip-slider {
  position: absolute;
  bottom: 56px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  background: rgba(15, 23, 42, 0.9);
  border-radius: 8px;
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: #e2e8f0;
  font-size: 13px;
}

.clip-slider input[type='range'] {
  width: 180px;
  accent-color: #38bdf8;
}

.lesion-panel {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 300px;
  max-height: calc(100% - 32px);
  overflow-y: auto;
  z-index: 20;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  padding: 12px;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.lesion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.lesion-header h3 {
  margin: 0;
  font-size: 14px;
  color: #f1f5f9;
}

.close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.close-btn:hover {
  color: #f1f5f9;
}

.lesion-desc {
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 10px;
  line-height: 1.5;
}

.compare-toggle {
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}

.compare-toggle button {
  flex: 1;
  padding: 4px 6px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 5px;
  background: transparent;
  color: #94a3b8;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}

.compare-toggle button.active {
  background: rgba(56, 189, 248, 0.15);
  border-color: #38bdf8;
  color: #38bdf8;
}

.compare-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compare-block {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.6;
}

.compare-block h4 {
  margin: 0 0 4px;
  font-size: 13px;
}

.compare-block.lesion {
  background: rgba(142, 68, 173, 0.15);
  border-left: 3px solid #8e44ad;
  color: #e9d5ff;
}

.compare-block.lesion h4 {
  color: #c084fc;
}

.compare-block.normal {
  background: rgba(39, 174, 96, 0.12);
  border-left: 3px solid #27ae60;
  color: #bbf7d0;
}

.compare-block.normal h4 {
  color: #4ade80;
}

.component-tooltip {
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 8px 14px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(251, 191, 36, 0.4);
  border-radius: 8px;
  color: #fbbf24;
  font-size: 13px;
  pointer-events: none;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>

<style>
.anatomy-hotspot-label {
  position: absolute;
  z-index: 5;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  color: #e2e8f0;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  transform: translateY(-50%);
  text-shadow:
    0 0 4px rgba(15, 23, 42, 0.95),
    0 1px 2px rgba(15, 23, 42, 0.9);
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.anatomy-hotspot-label--clickable {
  pointer-events: auto;
  cursor: pointer;
  color: #fbbf24;
}

.anatomy-hotspot-label--clickable:hover {
  color: #fde68a;
}
</style>
